import EventEmitter from '../Utils/EventEmitter.js'
import gsap from "gsap";

/**
 * Time class
 * Manages time tracking and animation loop
 */
export default class Time extends EventEmitter {
  // Static instance property
  static _instance = null;

  /**
   * Get the singleton instance of the class
   * @returns {Time} The singleton instance
   */
  static getInstance() {
    return Time._instance || new Time();
  }

  /**
   * Constructor
   */
  constructor() {
    // Ensure this is a singleton
    if (Time._instance) {
      return Time._instance;
    }

    super();

    Time._instance = this;

    // Setup
    this.start = Date.now();
    this.current = this.start;
    this.playing = true;
    this.elapsed = 0;
    this.delta = 0.016666666666666668;
    this.timeline = gsap.timeline({
      paused: true,
    });

    window.requestAnimationFrame(() => {
      this.tick();
    });
  }

  /**
   * Animation loop
   */
  tick() {
    const currentTime = Date.now();
    this.delta = Math.min((currentTime - this.current) * 0.001, 0.016);
    this.current = currentTime;
    this.elapsed = (this.current - this.start) * 0.001;

    if (this.delta > 0.06) {
      this.delta = 0.06;
    }

    this.timeline.time(this.elapsed);

    this.trigger('tick');

    window.requestAnimationFrame(() => {
      this.tick();
    });
  }

  /**
   * Reset time tracking
   */
  reset() {
    this.start = Date.now();
    this.current = this.start;
    this.elapsed = 0;
  }

  /**
   * Pause animation loop
   */
  pause() {
    this.playing = false;
    this.trigger('paused');
  }

  /**
   * Resume animation loop
   */
  resume() {
    this.playing = true;
    this.current = Date.now(); // Reset current time to prevent large delta
    this.trigger('resumed');
  }

  /**
   * Get elapsed time since start
   * @returns {Number} - Elapsed time in seconds
   */
  getElapsedTime() {
    return (Date.now() - this.start) * 0.001;
  }

  /**
   * Check if animation is paused
   * @returns {Boolean} - True if paused
   */
  isPaused() {
    return !this.playing;
  }

  /**
   * Stop animation loop and clean up
   */
  stop() {
    this.playing = false;
    this.trigger('stopped');
  }

  /**
   * Destroy the instance and clean up resources
   */
  destroy() {
    this.timeline.kill()
    Time._instance = null;
  }
}
